/**
 * Single source of truth for discriminating the tool-result envelope into
 * success vs. failure for downstream consumers.
 *
 * Tool `execute()` results are either a success body or a structured
 * failure payload (see `ToolOutputErrorSchema`:
 * `{ success: false, errorType, error, suggestions?, details?, recovery? }`).
 * The LLM transcript builder (`render-pipeline.tsx`), the in-UI "Show
 * details" view (`ToolResultDetails`) and the rerun debug hook all
 * discriminate the two through this helper so the sites cannot drift.
 *
 * On failure the parsed structured envelope is returned as `error` (for
 * the render pipeline's per-tool `message.error` formatter), and its
 * message is also surfaced flat as `errorMessage` for consumers that
 * only need a string.
 */

import { isToolErrorOutput, type ToolOutputError, ToolOutputErrorSchema } from './output-types.ts';

export type ResultEnvelope = { kind: 'success' } | { kind: 'failure'; errorMessage: string; error: ToolOutputError };

const FALLBACK_ERROR_MESSAGE = 'Tool call failed.';

/**
 * Inspect a tool-result `output` value and report whether it carries the
 * failure marker (`success: false`). Anything else — including missing
 * fields, plain objects, primitives — is treated as a success and the
 * caller is expected to read the value with the tool's own formatter.
 *
 * A failure that doesn't satisfy {@link ToolOutputErrorSchema} (malformed
 * payload) is normalised to a synthetic `UnknownError` envelope so every
 * failure consumer gets a well-formed {@link ToolOutputError}.
 */
export function parseResultEnvelope(value: unknown): ResultEnvelope {
  if (!isToolErrorOutput(value)) {
    return { kind: 'success' };
  }
  const parsed = ToolOutputErrorSchema.safeParse(value);
  const error: ToolOutputError = parsed.success
    ? parsed.data
    : { success: false, errorType: 'UnknownError', error: FALLBACK_ERROR_MESSAGE };
  return { kind: 'failure', errorMessage: error.error, error };
}
